import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Warehouse, RefreshCw, HelpCircle, Settings2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const getSettingsSections = (providerLabel = "Muhasebe") => ([
  {
    title: "Güncellemeler",
    items: [
      {
        key: "sync_name_to_store",
        label: "Ürün adı, açıklaması veya resimleri değiştiğinde mağazaya ilet",
        tooltip: "ObiHub'da ürün adı, açıklaması veya resimleri değiştirildiğinde WooCommerce mağazanıza otomatik olarak iletilir.",
      },
      {
        key: "sync_stock_to_store",
        label: "Ürün stok adetleri değiştiğinde mağazaya ilet",
        tooltip: "Stok miktarları değiştiğinde WooCommerce'deki stok otomatik güncellenir.",
      },
      {
        key: "sync_price_to_store",
        label: "Ürün fiyatı değiştiğinde mağazaya ilet",
        tooltip: "ObiHub'da fiyat değişikliği yapıldığında WooCommerce'deki fiyat otomatik güncellenir.",
      },
      {
        key: "return_order_restore_stock",
        label: "İade siparişi geldiğinde stoğu geri artır",
        tooltip: "Bir sipariş iade edildiğinde, ilgili ürünlerin stok miktarı otomatik olarak artırılır.",
      },
      {
        key: "expense_invoice_increase_stock",
        label: "Gider faturası çekildiğinde stoğu otomatik artır",
        tooltip: `${providerLabel} tarafında gider/alış faturası işlendiğinde ilgili ürünlerin stoğu otomatik artırılır.`,
      },
      {
        key: "sync_changes_to_accounting",
        label: `Ürün bilgileri, stok veya fiyatı değiştiğinde eşleşen ${providerLabel} ürününe ilet`,
        tooltip: `ObiHub'daki değişiklikler ${providerLabel} tarafındaki eşleştirilmiş ürüne otomatik olarak yansıtılır.`,
      },
    ],
  },
  {
    title: `${providerLabel} Üzerinden Yönet`,
    items: [
      {
        key: "manage_stock_from_accounting",
        label: `Ürün stok adedini ${providerLabel} üzerinden yönet`,
        tooltip: `Stok yönetimi ${providerLabel} tarafından yapılır. ObiHub ve mağaza stokları ${providerLabel}'a göre güncellenir.`,
      },
      {
        key: "manage_price_from_accounting",
        label: `Ürün fiyat bilgilerini ${providerLabel} üzerinden yönet`,
        tooltip: `Fiyat bilgileri ${providerLabel}'dan çekilir ve mağazaya iletilir.`,
      },
    ],
  },
  {
    title: "e-Posta Raporlamaları",
    items: [
      {
        key: "email_stock_diff_report",
        label: "Mağazadaki stok adedi farklı olan ürünler için e-postama günlük uyarı raporu gönder",
        tooltip: "Mağaza ile ObiHub arasında stok farkı olan ürünler için günlük e-posta bildirimi alırsınız.",
      },
    ],
  },
]);

function SettingRow({ setting, value, onChange }) {
  return (
    <div className="group flex items-center justify-between py-2.5">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Switch
          checked={value}
          onCheckedChange={onChange}
          data-testid={`setting-${setting.key}`}
        />
        <Label className="cursor-pointer text-sm leading-snug" onClick={() => onChange(!value)}>
          {setting.label}
        </Label>
      </div>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button className="shrink-0 ml-2 text-muted-foreground hover:text-foreground transition-colors">
              <HelpCircle className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[300px] text-xs">
            {setting.tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default function ProductSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentPlan = String(user?.subscription_plan || "").toLowerCase();
  const isProOrEnterprise = ["pro", "growth", "enterprise"].includes(currentPlan);
  const [settings, setSettings] = useState({
    sync_name_to_store: false,
    sync_stock_to_store: true,
    sync_price_to_store: false,
    return_order_restore_stock: true,
    expense_invoice_increase_stock: false,
    sync_changes_to_accounting: true,
    manage_stock_from_accounting: false,
    manage_price_from_accounting: false,
    email_stock_diff_report: false,
    selected_warehouse_id: null,
    selected_warehouse_name: null,
    store_images_in_obihub: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accountingProvider, setAccountingProvider] = useState("");
  const accountingProviderLabel = accountingProvider === "bizimhesap" ? "BizimHesap" : "Paraşüt";
  const settingsSections = useMemo(
    () => getSettingsSections(accountingProviderLabel),
    [accountingProviderLabel]
  );

  // Warehouses
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const warehousesRequestRef = useRef(0);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await api.get("/settings/product-settings");
      setSettings(prev => ({
        ...prev,
        ...data,
        sync_changes_to_accounting:
          data?.sync_changes_to_accounting ?? data?.sync_changes_to_parasut ?? prev.sync_changes_to_accounting,
        manage_stock_from_accounting:
          data?.manage_stock_from_accounting ?? data?.manage_stock_from_parasut ?? prev.manage_stock_from_accounting,
        manage_price_from_accounting:
          data?.manage_price_from_accounting ?? data?.manage_price_from_parasut ?? prev.manage_price_from_accounting,
      }));
    } catch { /* use defaults */ }
    finally { setLoading(false); }
  }, []);

  const fetchAccountingProvider = useCallback(async () => {
    try {
      const cfg = await api.get("/settings/accounting");
      const p = String(cfg?.provider || "").toLowerCase();
      if (p === "bizimhesap" || p === "parasut") {
        setAccountingProvider(p);
      } else if (cfg?.bizimhesap?.connected) {
        setAccountingProvider("bizimhesap");
      } else if (cfg?.parasut?.connected) {
        setAccountingProvider("parasut");
      } else {
        setAccountingProvider("");
      }
    } catch {
      setAccountingProvider("");
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    if (!accountingProvider) {
      setWarehouses([]);
      setWarehousesLoading(false);
      return;
    }
    const requestId = ++warehousesRequestRef.current;
    setWarehousesLoading(true);
    try {
      const normalizeRows = (data) => (
        Array.isArray(data)
          ? data
          : Array.isArray(data?.warehouses)
            ? data.warehouses
            : Array.isArray(data?.items)
              ? data.items
              : []
      );

      const data = accountingProvider === "bizimhesap"
        ? await api.get("/bizimhesap/warehouses")
        : await api.get("/parasut/warehouses");
      if (requestId === warehousesRequestRef.current) {
        setWarehouses(normalizeRows(data));
      }
    } catch {
      if (requestId === warehousesRequestRef.current) {
        setWarehouses([]);
      }
    } finally {
      if (requestId === warehousesRequestRef.current) {
        setWarehousesLoading(false);
      }
    }
  }, [accountingProvider]);

  useEffect(() => {
    fetchSettings();
    fetchAccountingProvider();
  }, [fetchSettings, fetchAccountingProvider]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings/product-settings", settings);
      toast.success(String(res?.message || "Ayarlar kaydedildi"));
    } catch (error) {
      toast.error(error.message || "Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="page-shell space-y-6" data-testid="product-settings-page">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/panel/urunler")} data-testid="back-btn" className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors shrink-0">
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          <div className="app-page-header">
            <h1 className="app-page-title">Ürün Ayarları</h1>
            <p className="app-page-subtitle">Ürün ve stok entegrasyonu davranışlarını yönetin.</p>
          </div>
        </div>
        <div className="page-header-actions">
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Kaydet
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left: Settings Form */}
        <div className="space-y-3 lg:col-span-2">
          {settingsSections.map((section) => (
            <Card key={section.title} className="rounded-2xl border-border/70 shadow-sm">
              <CardHeader className="pb-1.5 pt-4">
                <CardTitle className="text-base font-semibold">{section.title}</CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="divide-y">
                  {section.items.map((item) => (
                    <SettingRow
                      key={item.key}
                      setting={item}
                      value={settings[item.key]}
                      onChange={(val) => handleChange(item.key, val)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Right: Warehouses Panel */}
        <div className="space-y-3 lg:sticky lg:top-16 self-start">
          <Card className="rounded-2xl border-border/70 shadow-sm">
            <CardHeader className="pb-2 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Warehouse className="h-4 w-4" />
                  Depolar
                </CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={fetchWarehouses} disabled={warehousesLoading} data-testid="refresh-warehouses-btn">
                  {warehousesLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Stok yönetimi için kullanılacak depoyu seçin</p>
            </CardHeader>
            <CardContent className="pb-4">
              {warehousesLoading ? (
                <div className="py-8 text-center">
                  <Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" />
                  <p className="text-xs text-muted-foreground mt-2">Depolar yükleniyor...</p>
                </div>
              ) : warehouses.length === 0 ? (
                <div className="py-6 text-center">
                  <Warehouse className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">Depo bulunamadı</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {accountingProvider === "bizimhesap"
                      ? "BizimHesap bağlantınızı kontrol edin"
                      : "Paraşüt bağlantınızı kontrol edin"}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {warehouses.map((wh) => {
                    const isSelected = String(settings.selected_warehouse_id || "") === String(wh.id || "");
                    return (
                      <button
                        key={wh.id}
                        className={`w-full text-left rounded-md border-2 p-2.5 transition-all ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                        }`}
                        onClick={() => {
                          setSettings(prev => ({
                            ...prev,
                            selected_warehouse_id: wh.id,
                            selected_warehouse_name: wh.name,
                          }));
                        }}
                        data-testid={`warehouse-${wh.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? "border-primary" : "border-muted-foreground/40"
                            }`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium">#{wh.id} {wh.name}</p>
                              {wh.is_default && (
                                <p className="text-xs text-muted-foreground">Varsayılan depo</p>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <Badge className="text-xs bg-primary/10 text-primary border-primary/20">Seçili</Badge>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {!settings.selected_warehouse_id && warehouses.length > 0 && (
                    <p className="text-xs text-amber-600 mt-2">Lütfen stok yönetimi için bir depo seçin</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="rounded-lg border-blue-200 bg-blue-50/50 shadow-sm">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <Settings2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Entegrasyon Bilgisi</p>
                  <p className="text-xs text-blue-700 mt-1">
                    {accountingProvider === "bizimhesap"
                      ? "Depolar BizimHesap hesabınızdan otomatik olarak çekilir. Yeni depo ekledikten sonra bu alanı yenileyin."
                      : "Depolar Paraşüt hesabınızdan otomatik olarak çekilir. Yeni depo eklemek için Paraşüt panelinden depo oluşturun ve burada yenileyin."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/70 shadow-sm">
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                ObiHub Medya Saklama
                {isProOrEnterprise ? (
                  <Badge className="text-xs bg-emerald-100 text-emerald-800 border-emerald-200">Pro / Enterprise</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">Pro Gerekli</Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                WooCommerce’e göndermeden önce ürün görsellerini Cloudflare R2’ye yükleyip R2 URL’si ile iletir.
              </p>
            </CardHeader>
            <CardContent className="space-y-3 pb-4">
              <div className="flex items-center justify-between rounded-md border p-2.5">
                <div className="pr-3">
                  <p className="text-sm font-medium">Fotoğrafları ObiHub'da sakla</p>
                  <p className="text-xs text-muted-foreground">WooCommerce’e göndermeden önce görseller ObiHub R2 altyapısına alınır.</p>
                </div>
                <Switch
                  checked={Boolean(settings.store_images_in_obihub)}
                  onCheckedChange={(checked) => handleChange("store_images_in_obihub", checked)}
                  disabled={!isProOrEnterprise}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Bucket/domain eşleştirmeleri ObiHub super-admin panelinden merkezi olarak yönetilir.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
